import React from 'react';
import {
  Box,
  Select,
  MenuItem,
  TextField,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import Specialties from './Specialties';
import DateRelative from './DateRelative';

// Helper function to get sort options based on type and location
export const getSortOptions = (type, userLocation) => {
  const doctorsOptions = [
    { value: '-experience_years', label: 'Experienced Doctors' },
    { value: '-star_rating', label: 'Top Rated Doctor' },
    { value: 'consultation_fee', label: 'Low Consultation Fees' },
    ...(userLocation && (userLocation.latitude || userLocation.longitude) 
      ? [{ value: 'distance_km', label: 'Nearest to my location' }] 
      : [])
  ];
  
  const hospitalsOptions = [
    { value: '-star_rating', label: 'Rating wise' },
    ...(userLocation && (userLocation.latitude || userLocation.longitude) 
      ? [{ value: 'distance_km', label: 'Nearest to my location' }] 
      : [])
  ];
  
  const result = type === 'doctors' ? doctorsOptions : hospitalsOptions;
  
  // Safety check: always return at least one option
  if (result.length === 0) {
    return [{ value: '-star_rating', label: 'Rating wise' }];
  }
  
  return result;
};

const FiltersBar = ({
  type, // 'doctors' | 'hospitals'
  sortBy, // Now an array for multiple selections
  onSortChange, // Now handles array of selected options
  onSpecialtySelect,
  onViewAllSpecialities,
  onSearchChange,
  onFilterClick,
  searchTerm,
  searchBarOpen,
  onSearchBarToggle,
  userLocation,
  isMobile,
  selectedSpecialty,
  showDate = false,
  onDateRangeSelect,
}) => {
  const sortOptions = getSortOptions(type, userLocation);

  return (
    <>
      {/* Specialties Carousel */}
      <Box sx={{ mb: { xs: 1, md: 1.5 } }}>
        <Specialties 
          onSpecialtySelect={onSpecialtySelect}
          onViewAll={onViewAllSpecialities}
          selectedSpecialty={selectedSpecialty}
          noPadding={true}
        />
      </Box>

      {/* Sorting Dropdown and Filter Icon in Same Row */}
      {sortOptions && sortOptions.length > 0 && (
        <>
          {/* Sorting Checkboxes */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
              Sort by
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {sortOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={Array.isArray(sortBy) ? sortBy.includes(option.value) : sortBy === option.value}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        const currentSortBy = Array.isArray(sortBy) ? sortBy : (sortBy ? [sortBy] : []);

                        if (isChecked) {
                          // Add to array if not already present
                          if (!currentSortBy.includes(option.value)) {
                            onSortChange([...currentSortBy, option.value]);
                          }
                        } else {
                          // Remove from array
                          onSortChange(currentSortBy.filter(val => val !== option.value));
                        }
                      }}
                      size="small"
                      sx={{
                        color: 'primary.main',
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                      {option.label}
                    </Typography>
                  }
                  sx={{ minWidth: '45%', flex: '1 1 auto' }}
                />
              ))}
            </Box>
          </Box>

          {/* Filter Icon Row */}
          {/* <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            sx={{
              width: '100%',
              mb: 1,
            }}
          >
            <IconButton
              onClick={onFilterClick}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                borderRadius: '12px',
                width: 40,
                height: 40,
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.main',
                },
              }}
            >
              <FilterIcon sx={{ color: 'text.secondary', fontSize: '1.1rem' }} />
            </IconButton>
          </Box> */}

          {/* Search + Date (same row, mobile-friendly) */}
          <Box
            sx={{
              mb: { xs: 1, md: 1.5 },
              width: '100%',
              p: 0,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
            }}
          >
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={`Search ${type}...`}
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: 'background.paper',
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: 0,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          onSearchChange('');
                        }}
                        size="small"
                        sx={{
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <CloseIcon sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {showDate && (
              <Box
                sx={{
                  flex: '0 0 auto',
                  minWidth: { xs: 120, sm: 160, md: 200 },
                  maxWidth: { xs: 160, sm: 200, md: 260 },
                }}
              >
                <DateRelative
                  onDateRangeSelect={onDateRangeSelect}
                  navigateOnClick={false}
                />
              </Box>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default FiltersBar;

